import { render } from 'preact';
import { html } from './ui/common.js';
import { get } from './api.js';
import { Viewer } from './viewer/viewer.js';
import { me, setViewer, viewer, selection, loadBasemaps, chooseBasemap, loadModel, loadCaves, loadClubColors, loadCavePlaces, loadPlaces, loadGeology, loadNames, loadParcels, loadSinks, loadHydro, loadWater, loadChannels, loadCove, COVE_PARTS, sinkFilter, pollModel, opts, setOpts, basemaps, quality, QUALITY } from './store.js';
import { App } from './ui/app.js';
import { setCrs } from './geo.js';
import { Login, Invite } from './ui/auth.js';

const root = document.getElementById('root');

async function boot() {
  const m = location.hash.match(/^#\/invite\/(.+)$/);
  if (m) { render(html`<${Invite} token=${m[1]} onDone=${() => location.reload()} />`, root); return; }
  if (location.hash === '#/login') {            // the demo signs visitors in by itself; admins sign in here
    render(html`<${Login} onDone=${() => { history.replaceState(null, '', '/'); location.reload(); }} />`, root); return;
  }
  try { me.value = await get('/api/me'); }
  catch { render(html`<${Login} onDone=${() => location.reload()} />`, root); return; }
  document.title = me.value.site_name || 'Cave Atlas';
  render(html`<div class="loading">Loading the atlas…</div>`, root);
  const v = new Viewer(document.getElementById('scene'), document.getElementById('hovertip'));
  setViewer(v);
  if (location.search.includes('debug')) window.__atlasViewer = v;      // for performance checks
  v.setOptions(opts.value);
  v.on('select', (s) => { const f = me.value?.features || {}; if ((s.type !== 'cave' || f.cavepages !== false) && (s.type !== 'place' || f.places !== false)) selection.value = s; });
  try {
    v.setQuality(QUALITY[quality.value]);
    const tmeta = await get('/api/terrain'); setCrs(tmeta.crs);
    await v.loadTerrain(tmeta);
    await loadBasemaps();
    chooseBasemap(basemaps.value.find(b => b.key === 'hillshade')?.ready ? 'hillshade' : basemaps.value.find(b => b.ready)?.key);
  } catch (e) {
    render(html`<div class="loading">The terrain has not been installed yet (${e.message}). An admin needs to run the setup steps in the README.</div>`, root);
    return;
  }
  await loadPlaces();
  await loadClubColors();
  await loadCaves().catch(() => {});
  await loadModel();
  // parts of the atlas switched off for this member (Admin → Areas & features) are neither loaded nor shown
  const on = (k) => me.value?.features?.[k] !== false;
  if (!on('mapsheets')) setOpts({ mapMode: 'off', labels: { ...opts.value.labels, passages: false, leads: false } });
  if (!on('hydrology')) setOpts({ hydro: false, waterChannels: false, waterRoutes: false, waterLinks: false });
  if (!on('property')) setOpts({ parcels: false });
  if (on('geology')) await loadGeology();
  else if (opts.value.colorBy === 'formation') setOpts({ colorBy: 'depth' });
  if (viewer.M) setCrs(viewer.M.crs, viewer.M.convergence);
  if (on('places')) loadCavePlaces();
  if (on('names')) loadNames();
  if (on('property') && opts.value.parcels) loadParcels();
  if (on('hydrology')) loadHydro();           // small; also tells every details panel which basin a point drains to
  if (on('hydrology')) loadWater();           // streams in caves and the connections (station details use them)
  if (on('hydrology') && opts.value.waterChannels) loadChannels();
  if (on('sinks') && sinkFilter.value.show) loadSinks();
  if (COVE_PARTS.some(k => opts.value[k])) loadCove();
  if (!viewer.M) viewer.view('oblique', true);
  await pollModel().catch(() => {});
  render(html`<${App} />`, root);
}

window.addEventListener('atlas:unauthorized', () => { if (me.value) location.reload(); });
boot();
